import { AddToCart } from "./elements/cart/AddToCart";
import type { BillingCheckbox } from "./elements/checkout/BillingAddressCheckbox";
import { implementSearchBar } from "./elements/SearchResult";
import { noticeFailure } from "./notices";

/* DO NOT BOTHER with any regex more complicated than this. The only true way to
 * check if an email address is valid is to try sending an email.
 */
const EMAIL_REGEX = /^[^@]+@[^@]+\.[^@]+$/;

export type ObservedAttributes<C extends { observedAttributes: readonly string[] }> = C["observedAttributes"] extends readonly (infer T)[] ? T : never;

export const LOCAL_PICKUP_FIELD_NAMES = [
	"shipping_first_name",
	"shipping_last_name",
	"shipping_company",
	"shipping_phone",
];

export function udpdateReview() {
	const checkoutDeliveryElement = document.getElementById("checkout-delivery") as HTMLElement;
	const checkoutBillingElement = document.getElementById("checkout-billing") as HTMLElement;

	const shippingOption = (document.querySelector('input[name="shipping_method[0]"]:checked') as HTMLInputElement).id as "shipping-option" | "pickup-option";
	const deliveryInputs = checkoutDeliveryElement.querySelectorAll("select, input") as NodeListOf<HTMLInputElement | HTMLSelectElement>;
	const billingInputs = checkoutBillingElement.querySelectorAll("select, input") as NodeListOf<HTMLInputElement | HTMLSelectElement>;

	const billingCheckbox = document.querySelector<BillingCheckbox>("gct-billing-address-checkbox");
	const isBillingAddressSameAsDelivery = billingCheckbox?.checked ?? false;

	const reviewEmail = document.getElementById("email-review")!;
	const reviewDelivery = document.getElementById("delivery-info-review")!;
	const reviewDeliveryOption = document.getElementById("delivery-option-review")!;
	const reviewBilling = document.getElementById("billing-info-review")!;
	const reviewPayment = document.getElementById("payment-method-review")!;

	reviewEmail.textContent = (document.getElementById("billing_email") as HTMLInputElement).value;

	reviewDelivery.replaceChildren(...Array.from(deliveryInputs)
		.filter((input) => input.value.length > 0 && (shippingOption !== "pickup-option" || LOCAL_PICKUP_FIELD_NAMES.includes(input.name)))
		.map((input) => <div className="gct-text-lg">{inputDisplayValue(input)}</div>)
	);

	reviewDeliveryOption.textContent = shippingOption === "pickup-option" ? "Pick up" : "Delivery";

	if (isBillingAddressSameAsDelivery) {
		reviewBilling.replaceChildren(
			<div className="gct-text-lg"><em>Same as delivery</em></div>
		);
	} else {
		reviewBilling.replaceChildren(...Array.from(billingInputs)
			.filter((input) => input.value.length > 0)
			.map((input) => <div className="gct-text-lg">{inputDisplayValue(input)}</div>)
		);
	}

	const paymentOptionElemnt = document.querySelector<HTMLInputElement>('input[name="payment_method"]:checked');
	const paymentLabel = document.querySelector(`label[for="payment_method_${paymentOptionElemnt?.value ?? ""}"]`);

	if (paymentLabel !== null) {
		reviewPayment.innerHTML = paymentLabel.innerHTML;
	} else {
		// Payment lable does not exist when there is only one payment option
		(reviewPayment.parentNode as HTMLElement).classList.add("gct-d-none");
	}
}

function validateRegex(input: HTMLInputElement | HTMLSelectElement, regex: RegExp): boolean {
	if (!regex.test(input.value)) {
		input.classList.add("gct-input-error");
		return false;
	} else {
		input.classList.remove("gct-input-error");
		return true;
	}
}

function validateNonEmpty(input: HTMLInputElement | HTMLSelectElement): boolean {
	if (input.value.length === 0) {
		input.classList.add("gct-input-error");
		return false;
	} else {
		input.classList.remove("gct-input-error");
		return true;
	}
}

function inputDisplayValue(input: HTMLInputElement | HTMLSelectElement): string {
	if (input instanceof HTMLSelectElement) {
		const option = input.selectedOptions[0];
		return option?.textContent ?? "";
	}

	return input.value;
}

export function validateDeliveryForm() {
	const checkoutDeliveryElement = document.getElementById("checkout-delivery") as HTMLElement;

	const emailInput = document.getElementById("billing_email") as HTMLInputElement;
	const deliveryInputs = checkoutDeliveryElement.querySelectorAll("select, input") as NodeListOf<HTMLInputElement | HTMLSelectElement>;
	const isLocalPickup = (document.querySelector('input[name="shipping_method[0]"]:checked') as HTMLInputElement).id === "pickup-option";

	let isFormValid = validateRegex(emailInput, EMAIL_REGEX);
	for (const input of deliveryInputs) {
		if ("required" in input.dataset && (LOCAL_PICKUP_FIELD_NAMES.includes(input.name) || !isLocalPickup)) {
			/* DO NOT INLINE THIS VARIABLE
			 * If you inline `valid`, the computation will short-circuit (by
			 * definition of the && operator) and only the first invalid input
			 * will get a red border.
			 */
			const valid = validateNonEmpty(input);
			isFormValid &&= valid;
		}
	}

	if (!isFormValid) {
		noticeFailure("Please provide all the necessary details");
	}

	return isFormValid;
}

export function validateBillingForm() {
	const billingCheckbox = document.querySelector<BillingCheckbox>("gct-billing-address-checkbox");
	const isBillingAddressSameAsDelivery = billingCheckbox?.checked ?? false;

	if (isBillingAddressSameAsDelivery) {
		return true;
	}

	const checkoutBillingElement = document.getElementById("checkout-billing") as HTMLElement;
	const billingInputs = checkoutBillingElement.querySelectorAll("select, input") as NodeListOf<HTMLInputElement | HTMLSelectElement>;

	let isFormValid = true;
	for (const input of billingInputs) {
		if ("required" in input.dataset) {
			/* DO NOT INLINE THIS VARIABLE
			 * If you inline `valid`, the computation will short-circuit (by
			 * definition of the && operator) and only the first invalid input
			 * will get a red border.
			 */
			const valid = validateNonEmpty(input);
			isFormValid &&= valid;
		}
	}

	if (!isFormValid) {
		noticeFailure("Please provide all the necessary details");
	}

	return isFormValid;
}

export function implementArrowNavigation() {
	const prevButton = document.getElementById("carousel-arrow-prev");
	const nextButton = document.getElementById("carousel-arrow-next");

	function handleCarouselMoveNext() {
		const carousel = document.getElementById("cross-sells-container")
		const slide = document.querySelector(".gct-cross-sell");

		const slideWidth = slide!.clientWidth;
		carousel!.scrollLeft = carousel!.scrollLeft + slideWidth;
		(prevButton as HTMLButtonElement).disabled = false;

		const maxScrollLeft = carousel!.scrollWidth - carousel!.clientWidth;
		if (carousel!.scrollLeft >= maxScrollLeft) {
			(nextButton as HTMLButtonElement).disabled = true;
		}

	}

	function handleCarouselMovePrev() {
		const carousel = document.getElementById("cross-sells-container")
		const slide = document.querySelector(".gct-cross-sell");
		const slideWidth = slide!.clientWidth;
		carousel!.scrollLeft = carousel!.scrollLeft - slideWidth;
		(nextButton as HTMLButtonElement).disabled = false;

		if (carousel!.scrollLeft <= 0) {
			(prevButton as HTMLButtonElement).disabled = true;
		}
	}

	prevButton?.addEventListener("click", handleCarouselMovePrev);
	nextButton?.addEventListener("click", handleCarouselMoveNext);
};

export function implementArrowNavigation_gen(carouselId: string, slideId: string, prveButtonId: string, nextButtonId: string) {
	const prevButton = document.getElementById(prveButtonId);
	const nextButton = document.getElementById(nextButtonId);

	function handleCarouselMoveNext() {
		const carousel = document.getElementById(carouselId)
		const slide = document.querySelector(slideId);
		const slideWidth = slide!.clientWidth;
		carousel!.scrollLeft = carousel!.scrollLeft + slideWidth;
		(prevButton as HTMLButtonElement).disabled = false;

		const maxScrollLeft = carousel!.scrollWidth - carousel!.clientWidth;
		if (carousel!.scrollLeft >= maxScrollLeft) {
			(nextButton as HTMLButtonElement).disabled = true;
		}

	}

	function handleCarouselMovePrev() {
		const carousel = document.getElementById(carouselId)
		const slide = document.querySelector(slideId);
		const slideWidth = slide!.clientWidth;
		carousel!.scrollLeft = carousel!.scrollLeft - slideWidth;
		(nextButton as HTMLButtonElement).disabled = false;

		if (carousel!.scrollLeft <= 0) {
			(prevButton as HTMLButtonElement).disabled = true;
		}
	}

	prevButton?.addEventListener("click", handleCarouselMovePrev);
	nextButton?.addEventListener("click", handleCarouselMoveNext);
};

export function collectionNavigation() {
	const prevButton = document.getElementById("collection-carousel-arrow-prev");
	const nextButton = document.getElementById("collection-carousel-arrow-next");

	let scrollControll = 0;

	function handleCarouselMoveNext() {
		const carousel = document.getElementById("collections-names-container")

		carousel!.scrollLeft = carousel!.scrollLeft + 100;
		scrollControll += 100;
		(prevButton as HTMLButtonElement).disabled = false;

		if (carousel!.scrollLeft < scrollControll) {
			(nextButton as HTMLButtonElement).disabled = true;
		}

	}

	function handleCarouselMovePrev() {
		const carousel = document.getElementById("collections-names-container")

		carousel!.scrollLeft = carousel!.scrollLeft - 100;
		scrollControll -= 100;
		(nextButton as HTMLButtonElement).disabled = false;

		if (carousel!.scrollLeft <= 0) {
			(prevButton as HTMLButtonElement).disabled = true;
		}
	}

	prevButton?.addEventListener("click", handleCarouselMovePrev);
	nextButton?.addEventListener("click", handleCarouselMoveNext);
};

export function showMinicart() {
	const minicart = document.getElementById("mini-cart-container");
	const overlay = document.getElementById("overlay");

	if (minicart) {
		minicart.classList.remove("gct-header-mini-basket");
	}

	if (overlay) {
		overlay.style.zIndex = "110";
		overlay.classList.remove("gct-overlay-hidden");
	}

	localStorage.setItem("showMiniBasket", "true");
}

export function myFunction(containerID: string, buttonId: string) {
	const x = document.getElementById(containerID);
	const button = document.getElementById(buttonId);

	function myFunction() {

		if (!x) return;
		if (!button) return;


		if (x.style.display === "flex") {
			button.style.transform = "rotate(0deg)";
			x.style.display = "none";

		} else {
			button.style.transform = "rotate(180deg)";
			x.style.display = "flex";
		}
	}


	button?.addEventListener("click", myFunction);
}

export function hideMinicart() {
	const minicart = document.getElementById("mini-cart-container");
	const overlay = document.getElementById("overlay");

	if (minicart) {
		minicart.classList.add("gct-header-mini-basket");
	}

	if (overlay) {
		overlay.classList.add("gct-overlay-hidden");
		overlay.style.zIndex = "90";
	}
}

export function implementMiniCart() {
	const basketButton = document.getElementById("header-basket-button");
	const isCart = basketButton?.dataset.isCart;
	if (basketButton && !isCart) {
		basketButton.addEventListener('click', showMinicart);
	}
}

export function updateDisplay() {
	implementArrowNavigation();
	implementSearchBar();
	implementMiniCart();
	implementSingleProductOverlay();
	implementVariationSelection();
}

/**
 * Generates a new, unique ID suitable for `id` attribute of an HTML element.
 */
export const genId = (() => {
	let nextId = 0;
	return (): string => {
		const id = `gct-gen-${nextId}`;
		nextId += 1;
		return id;
	};
})();

export function headerCustomBehaviour() {
	const header = document.getElementById("header");
	const isFrontPage = header?.getAttribute("data-is-front-page") === "true";

	if (isFrontPage) {
		header.classList.add("header-fornt-page");

		document.addEventListener("scroll", () => {
			if (window.scrollY > 0) {
				header.classList.remove("header-fornt-page");
			} else {
				header.classList.add("header-fornt-page");
			}
		});
	}


/* 	const mobileHeader = document.getElementById("mobile-header");
	const isFrontPagemobile = mobileHeader?.getAttribute("data-is-front-page") === "true";

	if (isFrontPagemobile) {
		mobileHeader.classList.add("header-fornt-page");

		document.addEventListener("scroll", () => {
			if (window.scrollY > 0) {
				mobileHeader.classList.remove("header-fornt-page");
			} else {
				mobileHeader.classList.add("header-fornt-page");
			}
		});
	} */
}

export function singleProductPhotosMobileBehaviour() {
	const carousel = document.getElementById("product-carousel") as HTMLElement;

	if (!carousel) return;

	let isDragging = false;
	let startX = 0;
	let scrollLeft = 0;

	carousel.addEventListener('mousedown', (e) => {
		isDragging = true;
		carousel.classList.add('gct-active');
		startX = e.pageX - carousel.offsetLeft;
		scrollLeft = carousel.scrollLeft;
	});

	carousel.addEventListener('mouseup', () => {
		isDragging = false;
		carousel.classList.remove('gct-active');
	});

	carousel.addEventListener('mouseleave', () => {
		isDragging = false;
		carousel.classList.remove('gct-active');
	});

	carousel.addEventListener('mousemove', (e) => {
		if (!isDragging) return;
		e.preventDefault();
		const x = e.pageX - carousel.offsetLeft;
		const walk = (x - startX) * 2;
		carousel.scrollLeft = scrollLeft - walk;
	});
}

export function toggleMobileHeader() {
	const burgerButton = document.getElementById('burger');
	const mobileHeaderContainer = document.getElementById("burger-content") as HTMLElement;
	const showHeaderButtons = document.getElementsByName('show-header');
	const mobileHeader = document.querySelector(".gct-header-mobile") as HTMLElement;

	const shopContent = document.getElementById("shop-content") as HTMLElement;
	const shopBtn = document.getElementById("show-shop");

	const glasswareContent = document.getElementById("glassawre-content") as HTMLElement;
	const glassawreBtn = document.getElementById("show-glassware");

	const decanterContent = document.getElementById("decanters-content") as HTMLElement;
	const decanterBtn = document.getElementById("show-decanters");

	const accesoriesContent = document.getElementById("accessories-content") as HTMLElement;
	const accesoriesBtn = document.getElementById("show-accessories");

	const collectionsContent = document.getElementById("collections-content") as HTMLElement;
	const collectionsBtn = document.getElementById("show-collections");

	const giftsContent = document.getElementById("gifts-content") as HTMLElement;
	const giftsBtn = document.getElementById("show-gifts");

	const personalisationContent = document.getElementById("personalisation-content") as HTMLElement;
	const personalisationBtn = document.getElementById("show-personalisation");

	const businessContent = document.getElementById("business-content") as HTMLElement;
	const businessBtn = document.getElementById("show-business");

	const searchContent = document.getElementById("search-content");

	const prevHeaderBtns = document.getElementsByName("prev-header-first");
	const prevShopBtns = document.getElementsByName('prev-header-second-shop');

	if (burgerButton && mobileHeaderContainer && mobileHeader && shopContent && shopBtn && collectionsContent && collectionsBtn
		&& glassawreBtn && glasswareContent && decanterContent && decanterBtn && accesoriesContent && searchContent
		&& accesoriesBtn && giftsContent && giftsBtn && personalisationContent && personalisationBtn && businessBtn && businessContent) {

		burgerButton.addEventListener('click', () => {
			mobileHeader.classList.add("gct-d-none");
			mobileHeaderContainer.classList.remove("gct-d-none");
		});

		shopBtn.addEventListener("click", () => {
			mobileHeaderContainer.classList.add("gct-d-none");
			shopContent.classList.remove("gct-d-none");
		});

		giftsBtn.addEventListener("click", () => {
			mobileHeaderContainer.classList.add("gct-d-none");
			giftsContent.classList.remove("gct-d-none");
		});

		personalisationBtn.addEventListener("click", () => {
			mobileHeaderContainer.classList.add("gct-d-none");
			personalisationContent.classList.remove("gct-d-none");
		});

		businessBtn.addEventListener("click", () => {
			mobileHeaderContainer.classList.add("gct-d-none");
			businessContent.classList.remove("gct-d-none");
		});

		glassawreBtn.addEventListener("click", () => {
			shopContent.classList.add("gct-d-none");
			glasswareContent.classList.remove("gct-d-none");
		});

		decanterBtn.addEventListener("click", () => {
			shopContent.classList.add("gct-d-none");
			decanterContent.classList.remove("gct-d-none");
		});

		accesoriesBtn.addEventListener("click", () => {
			shopContent.classList.add("gct-d-none");
			accesoriesContent.classList.remove("gct-d-none");
		});

		collectionsBtn.addEventListener("click", () => {
			shopContent.classList.add("gct-d-none");
			collectionsContent.classList.remove("gct-d-none");
		})


		for (const btn of showHeaderButtons) {
			btn.addEventListener("click", () => {
				mobileHeaderContainer.classList.add("gct-d-none");
				shopContent.classList.add("gct-d-none");
				giftsContent.classList.add("gct-d-none");
				personalisationContent.classList.add("gct-d-none");
				businessContent.classList.add("gct-d-none");
				glasswareContent.classList.add("gct-d-none");
				decanterContent.classList.add("gct-d-none");
				accesoriesContent.classList.add("gct-d-none");
				collectionsContent.classList.add("gct-d-none");
				searchContent.classList.add("gct-d-none");
				mobileHeader.classList.remove("gct-d-none");
			})
		}

		for (const prevBtn of prevHeaderBtns) {
			prevBtn.addEventListener("click", () => {
				shopContent.classList.add("gct-d-none");
				giftsContent.classList.add("gct-d-none");
				personalisationContent.classList.add("gct-d-none");
				businessContent.classList.add("gct-d-none");
				mobileHeaderContainer.classList.remove("gct-d-none");
			})
		}

		for (const prevShopBtn of prevShopBtns) {
			prevShopBtn.addEventListener("click", () => {
				glasswareContent.classList.add("gct-d-none");
				decanterContent.classList.add("gct-d-none");
				accesoriesContent.classList.add("gct-d-none");
				collectionsContent.classList.add("gct-d-none");
				shopContent.classList.remove("gct-d-none");
			})
		}
	}
}

export function implementSingleProductOverlay() {
	const closeButton = document.getElementById('close-single-product-overlay');

	closeButton?.addEventListener("click", function () {
		const overlay = document.getElementById('single-product-overlay');

		overlay?.classList.add("gct-d-none");
	});


	const openButton = document.getElementById('open-single-product-overlay');

	openButton?.addEventListener("click", function () {
		const overlay = document.getElementById('single-product-overlay');

		overlay?.classList.remove("gct-d-none");
	});
}

interface ProductVariation {
	readonly attributes: Readonly<Record<string, string>>;
	readonly price_html: string;
	readonly sku: string;
	readonly variation_id: number;
	/** Empty string when no image. */
	readonly image_src: string;
}

function implementVariationSelection() {
	const productVariationsElement = document.getElementById("product-variations");
	if (productVariationsElement === null) return;

	const overlayImageElement = document.getElementById("gct-overlay-image") as HTMLImageElement;
	const largeImageElement = document.getElementById("gct-large-image") as HTMLImageElement;
	const carouselImageElement = document.getElementById("gct-carousel-image") as HTMLImageElement;

	const productSkuElement = document.getElementById("product-sku") as HTMLElement;
	const productPriceElement = document.getElementById("product-price") as HTMLElement;
	const addToCartElement = (document.getElementsByTagName("gct-add-to-cart") as HTMLCollectionOf<AddToCart>)[0];

	const defaultSku = productSkuElement.textContent;
	const defaultPriceHtml = productPriceElement.innerHTML;

	const defaultOverlayImageSrc = overlayImageElement.src;
	const defaultLargeImageSrc = largeImageElement.src;
	const defaultLargeImageSrcset = largeImageElement.srcset;
	const defaultLargeImageSizes = largeImageElement.sizes;
	const defaultCarouselImageSrc = carouselImageElement.src;

	const productVariations = JSON.parse(productVariationsElement.textContent!) as readonly ProductVariation[];
	const attributeSelectElements = Array.from(document.querySelectorAll("select[data-attribute_name]")) as readonly HTMLSelectElement[];

	const getAttributeSet = (): Readonly<Record<string, string>> => Object.freeze(Object.fromEntries(
		attributeSelectElements.map((attributeSelect) => [
			attributeSelect.dataset["attribute_name"]!,
			attributeSelect.value,
		] as const),
	));

	const findVariation = (attributeSet: Readonly<Record<string, string>>): ProductVariation | undefined => {
		const ka = Object.keys(attributeSet);
		for (const productVariation of productVariations) {
			const kb = Object.keys(productVariation.attributes);
			if (ka.length === kb.length && ka.every((k) => productVariation.attributes[k] === attributeSet[k])) {
				return productVariation;
			}
		}

		return undefined;
	};

	const onAttributeSelect = function () {
		const attributeSet = getAttributeSet();

		largeImageElement.removeAttribute("srcset");
		largeImageElement.removeAttribute("sizes");

		if (Object.values(attributeSet).some((value) => value === "")) {
			// Not all attributes are selected.
			productSkuElement.textContent = defaultSku;
			productPriceElement.innerHTML = defaultPriceHtml;
			addToCartElement.setAttribute("disabled", "");
			delete addToCartElement.dataset.variationId;
			overlayImageElement.src = defaultOverlayImageSrc;
			largeImageElement.src = defaultLargeImageSrc;
			largeImageElement.srcset = defaultLargeImageSrcset;
			largeImageElement.sizes = defaultLargeImageSizes;
			carouselImageElement.src = defaultCarouselImageSrc;
			return;
		}

		const productVariation = findVariation(attributeSet);

		if (productVariation === undefined) {
			// All attributes are selected, but no variation exists.
			productSkuElement.textContent = "N/A";
			productPriceElement.innerHTML = "N/A";
			addToCartElement.setAttribute("disabled", "");
			delete addToCartElement.dataset.variationId;
			overlayImageElement.src = defaultOverlayImageSrc;
			largeImageElement.src = defaultLargeImageSrc;
			largeImageElement.srcset = defaultLargeImageSrcset;
			largeImageElement.sizes = defaultLargeImageSizes;
			carouselImageElement.src = defaultCarouselImageSrc;

		} else {
			productSkuElement.textContent = productVariation.sku;
			productPriceElement.innerHTML = productVariation.price_html || defaultPriceHtml;
			addToCartElement.removeAttribute("disabled");
			addToCartElement.dataset.variationId = String(productVariation.variation_id);

			if (productVariation.image_src) {
				overlayImageElement.src = productVariation.image_src;
				largeImageElement.src = productVariation.image_src;
				carouselImageElement.src = productVariation.image_src;
			} else {
				overlayImageElement.src = defaultOverlayImageSrc;
				largeImageElement.src = defaultLargeImageSrc;
				largeImageElement.srcset = defaultLargeImageSrcset;
				largeImageElement.sizes = defaultLargeImageSizes;
				carouselImageElement.src = defaultCarouselImageSrc;
			}
		}
	};

	attributeSelectElements.forEach((e) => e.addEventListener("change", onAttributeSelect));
	onAttributeSelect();
}
